import { FieldWrapper } from "@progress/kendo-react-form";
import { Switch, SwitchProps } from "@progress/kendo-react-inputs";
import { Error, Hint, Label } from "@progress/kendo-react-labels";
import * as React from "react";
import { useControlledField, type FieldValidator } from "./useControlledField";

export interface FormSwitchProps
  extends Omit<SwitchProps, "name" | "value" | "onChange" | "valid"> {
  name: string;
  validator?: FieldValidator;
  label?: string;
  hint?: string;
  showErrorOnPristine?: boolean;
}

export const FormSwitch: React.FC<FormSwitchProps> = ({
  name,
  validator,
  label,
  hint,
  showErrorOnPristine = false,
  disabled,
  ...switchProps
}) => {
  const { field, fieldState } = useControlledField({ name, validator });
  const showError =
    !!fieldState.error && (showErrorOnPristine || fieldState.isTouched);
  const showHint = !!hint;
  const inputId = name;
  const hintId = showHint ? `${inputId}_hint` : "";
  const errorId = showError ? `${inputId}_error` : "";
  const labelId = label ? `${inputId}_label` : "";

  return (
    <FieldWrapper>
      {label && (
        <Label
          id={labelId}
          editorId={inputId}
          editorValid={!fieldState.error}
          editorDisabled={!!disabled}
          className="tlx-form-switch-label"
        >
          {label}
        </Label>
      )}
      <Switch
        {...switchProps}
        id={inputId}
        name={name}
        disabled={disabled}
        ariaDescribedBy={`${hintId} ${errorId}`}
        valid={!fieldState.error}
        checked={!!field.value}
        onChange={e => field.onChange(e.value)}
        onBlur={field.onBlur}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showError && <Error id={errorId}>{fieldState.error?.message}</Error>}
    </FieldWrapper>
  );
};

export default FormSwitch;
